// External dependencies.
import { LitElement, html } from "lit";
import { __, sprintf } from "@wordpress/i18n";
import $ from "jquery";

/**
 * AppDiscountCouponForm component class.
 *
 * @since 0.5.0
 * @class AppDiscountCouponForm
 * @extends {LitElement}
 */
class AppDiscountCouponForm extends LitElement {
    /**
     * Creates an instance of AppDiscountCouponForm.
     * 
     * @since 0.15.0
     * @memberof AppDiscountCouponForm
     */
    constructor() {
        super();
        this.isSubmitting = false;
        this.errors = {};
        this.handleSubmit = this.handleSubmit.bind( this );
        this.handleSuccess = this.handleSuccess.bind( this );
        this.handleError = this.handleError.bind( this );
    }

    /**
     * Inits the component's properties.
     *
     * @since 0.5.0
     * @since 0.15.0 Added the properties: isSubmitting, errors.
     * @readonly
     * @static
     * @memberof AppDiscountCouponForm
     */
    static get properties() {
        return {
            recaptchaSitekey: { type: String },
            nonce: { type: String },
            isSubmitting: { type: Boolean },
            errors: { type: Object },
        };
    }

    /**
     * Handles the form element submit event.
     *
     * @since 0.15.0
     * @param object e
     * @memberof AppDiscountCouponForm
     */
    handleSubmit( e ) {
        e.preventDefault();
        this.isSubmitting = true;
        const form = e.target;
        const url = form.getAttribute( 'action' );
        const formdata = new FormData( form );

        fetch( url, {
            method: 'POST',
            body: formdata,
        } )
            .then( res => res.json() )
            .then( this.handleSuccess )
            .catch( this.handleError );
    }

    /**
     * Handles the form success action.
     *
     * @since 0.15.0
     * @since 1.5.0 Changed sweet alert content for the success response.
     * @param object res
     * @memberof AppDiscountCouponForm
     */
    handleSuccess( res ) {
        this.isSubmitting = false;
        grecaptcha.reset();

        if ( res.success ) {
            this.errors = {};
            this.querySelector( 'form' ).reset();
            $( '#ts-coupon-form-modal' ).modal( 'hide' );
            const { discount, code } = res.data;
            const content = document.createElement( 'div' );
            content.innerHTML = `
                <p class="text-center">${sprintf( __( '¡Obtén %s de descuento!', 'tio-simon-foods' ), discount )}</p>
                <p class="text-center">${__( 'Usa el siguiente código al finalizar la compra:', 'tio-simon-foods' )}</p>
                <p class="h2 font-bold text-center mb-0">${code}</p>
            `;

            swal( {
                // title: window.app.name,
                // text: res.data,
                content,
                icon: 'success',
                buttons: {
                    confirm: {
                        className: 'btn btn-ts-primary',
                    },
                },
            } ).then( () => content.remove() );

            return;
        }

        if ( res.data.has_errors ) {
            this.errors = res.data.errors;
            return;
        }

        const text = ( 0 === res.data ) ? window.app.error_message : res.data;

        this.querySelector( 'form' ).reset();
        $( '#ts-coupon-form-modal' ).modal( 'hide' );
        swal( {
            title: window.app.name,
            text: text,
            icon: 'warning',
            buttons: {
                confirm: {
                    className: 'btn btn-ts-primary',
                },
            },
        } );
    }

    /**
     * Handles the form error action.
     *
     * @since 0.15.0
     * @param object err
     * @memberof AppDiscountCouponForm
     */
    handleError( err ) {
        console.log( err );
        this.isSubmitting = false;
        grecaptcha.reset();

        swal( {
            title: window.app.name,
            text: window.app.error_message,
            icon: 'error',
            buttons: {
                confirm: {
                    className: 'btn btn-ts-primary',
                },
            },
        } );
    }

    /**
     * Renders the component.
     *
     * @since 0.5.0
     * @returns TemplateResult
     * @memberof AppDiscountCouponForm
     */
    render() {
        return html`
            <form action="${window.app.ajax_url}" method="POST" @submit="${this.handleSubmit}">
                <input type="hidden" name="action" value="handle_discount_coupon_form">
                <input type="hidden" name="_wpnonce" value="${this.nonce}">
                <div class="form-group">
                    <label for="fullname" class="sr-only">${__( 'Nombre Completo', 'tio-simon-foods' )}</label>
                    <input type="text" class="form-control form-control-lg text-center" minlength="3" name="fullname" id="fullname" placeholder="${__( 'Nombre Completo', 'tio-simon-foods' )}" required>
                    ${this.errors.fullname ? html`<div class="form-error">${this.errors.fullname}</div>` : null}
                </div>
                <div class="form-group">
                    <label for="email" class="sr-only">${__( 'Email', 'tio-simon-foods' )}</label>
                    <input type="email" class="form-control form-control-lg text-center" name="email" id="email" placeholder="${__( 'Email', 'tio-simon-foods' )}" required>
                    ${this.errors.email ? html`<div class="form-error">${this.errors.email}</div>` : null}
                </div>
                <div class="row align-items-center">
                    <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
                        <div class="g-recaptcha mb-3 mb-md-0" data-sitekey="${this.recaptchaSitekey}"></div>
                    </div>
                    <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mt-3">
                        <button type="submit" class="btn btn-ts-primary btn-block btn-lg" ?disabled="${this.isSubmitting}">${this.isSubmitting ? __( 'Envíando...', 'tio-simon-foods' ) : __( 'Enviar', 'tio-simon-foods' )}</button>
                    </div>
                </div>
            </form>
        `;
    }

    /**
     * Renders the template in the light DOM.
     *
     * @since 0.5.0
     * @returns this
     * @memberof AppDiscountCouponForm
     */
    createRenderRoot() {
        return this;
    }
}

window.customElements.define( 'app-discount-coupon-form', AppDiscountCouponForm );
